import {
  HeliosHotkeysType,
  HighlightRowsInAccountList,
  HighlightRowsInCWTransactions,
  HighlightRowsInExtTransactions,
  HighlightRowsInExternalFileViewer,
  HotkeyMetadata,
} from '@helios/hotkeys'
import { AccountListKey } from '@helios/panels/account-list'
import { ClearwaterTransactionsKey } from '@helios/panels/clearwater-transactions'
import { DistributionsPanelConfig } from '@helios/panels/distributions'
import { ExternalFileViewerKey } from '@helios/panels/external-file-viewer'
import { ExternalTransactionsKey } from '@helios/panels/external-transactions'
import { MultiBreaksKey } from '@helios/panels/multi-breaks'
import { environment } from '../../../../../environments/environment'

export const HotkeysInDevelopmentAndStaging: HeliosHotkeysType[] = []

const HeliosHotkeys: [HeliosHotkeysType, HotkeyMetadata][] = [
  [
    'global-cheatSheet-help',
    {
      key: 'alt+0',
      name: 'Show/Hide this help menu',
    },
  ],
  [
    'global-create-session',
    {
      key: 'alt+n',
      name: 'Create new session',
    },
  ],
  [
    'global-delete-active-session',
    {
      key: 'alt+shift+delete',
      name: 'Delete active session',
    },
  ],
  [
    'global-layout-lock',
    {
      key: 'ctrl+l',
      name: 'Lock/Unlock Layout',
      description: 'Lock/Unlock moving of panels in the layout',
    },
  ],
  [
    'toggle-panel-catalog',
    {
      key: 'alt+x',
      name: 'Open/Close Panel Catalog',
      description: 'Will open or close the Panel Catalog drawer.',
    },
  ],
  [
    'toggle-workspace-catalog',
    {
      key: 'alt+shift+w',
      name: 'Open/Close Workspace Catalog',
      description: 'Will open or close the Workspace Catalog drawer.',
    },
  ],
  [
    'toggle-sessions-drawer',
    {
      key: 'alt+shift+d',
      name: 'Open/Close Sessions Drawer',
      description: 'Will open or close the Sessions Drawer.',
    },
  ],
  [
    'focus-breaks',
    {
      key: 'alt+b',
      name: 'Focus Breaks & Variances Panel',
      description: 'Will open or focus Breaks Panel.',
    },
  ],
  [
    'focus-accounts',
    {
      key: 'alt+a',
      name: 'Focus Accounts Panel',
    },
  ],
  [
    'focus-transactions-panel',
    {
      key: 'alt+t',
      name: 'Focus Transaction Panel',
      description: 'Will open or focus Transactions Panel.',
    },
  ],
  [
    'focus-positions',
    {
      key: 'alt+p',
      name: 'Focus Positions Panel',
      description:
        'Allows for examination of both Clearwater and external positions.',
    },
  ],
  [
    'focus-distributions',
    {
      key: 'alt+k',
      name: 'Focus Distributions',
      description: DistributionsPanelConfig.description,
    },
  ],
  [
    'focus-external-file-manager',
    {
      key: 'alt+r',
      name: 'Focus External File Manager Panel',
      description: 'Will open or focus External Files Manager panel.',
    },
  ],
  [
    'focus-pricing',
    {
      key: 'alt+e',
      name: 'Focus Pricing Panel',
      description: 'Will open or focus Pricing panel.',
    },
  ],
  [
    'focus-corporate-actions',
    {
      key: 'alt+q',
      name: 'Focus Corporate Actions Panel',
      description: 'Will open or focus Corporate Actions panel.',
    },
  ],
  [
    'focus-jobs',
    {
      key: 'alt+j',
      name: 'Focus Jobs Panel',
      description: 'Will open or focus Jobs panel.',
    },
  ],
  [
    'run-without-etl',
    {
      key: 'alt+s',
      name: 'Run without ETL',
      description: 'Will run the session without ETLs.',
    },
  ],
  [
    'run-with-etl',
    {
      key: 'alt+shift+s',
      name: 'Run with ETL',
      description: 'Will run the session with ETLs.',
    },
  ],
  [
    'delete-selected-transaction',
    {
      key: 'alt+backspace',
      name: 'Delete selected Clearwater transaction',
      description: 'Will delete the selected Clearwater transaction.',
      options: {
        scope: ClearwaterTransactionsKey,
      },
    },
  ],
  [
    'hold-selected-breaks-1',
    {
      key: 'alt+1',
      name: 'Hold selected breaks for 1 day',
      description:
        'Will bring up the hold modal for selected breaks set to 1 day.',
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    'hold-selected-breaks-2',
    {
      key: 'alt+2',
      name: 'Hold selected breaks for 2 days',
      description:
        'Will bring up the hold modal for selected breaks set to 2 days.',
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    'hold-selected-breaks-3',
    {
      key: 'alt+3',
      name: 'Hold selected breaks for 3 days',
      description:
        'Will bring up the hold modal for selected breaks set to 3 days.',
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    'hold-selected-breaks-4',
    {
      key: 'alt+4',
      name: 'Hold selected breaks for 4 days',
      description:
        'Will bring up the hold modal for selected breaks set to 4 days.',
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    'hold-selected-breaks-5',
    {
      key: 'alt+5',
      name: 'Hold selected breaks for 5 days',
      description:
        'Will bring up the hold modal for selected breaks set to 5 days.',
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    'accept-selected-break',
    {
      key: 'alt+w',
      name: 'Accept Selected Break',
      description: 'Will bring up the Accept modal for selected breaks.',
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    'copy-break-codes',
    {
      key: 'alt+c',
      name: 'Copy Break Codes',
      description:
        "Copies the selected break's break codes and note, ready to paste into a break dialog.",
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    // Help-menu metadata ONLY — nothing binds this. Paste has to fire while the user
    // is inside a dialog's form fields, which hotkeys-js' default filter drops, so each
    // break dialog listens for it directly (see isPasteBreakCodesEvent, which owns the
    // real alt+v check). Changing `key` below updates the cheat sheet and NOT the
    // behaviour. Because bind() is never called, this entry shows as DISABLED in the
    // cheat sheet; that is known and accepted.
    'paste-break-codes',
    {
      key: 'alt+v',
      name: 'Paste Break Codes',
      description:
        'Pastes the copied break codes and note into the focused break dialog.',
      options: {
        scope: MultiBreaksKey,
      },
    },
  ],
  [
    'find-matched-ext-trans',
    {
      key: 'alt+m',
      name: 'Find matched external transactions',
      description:
        'Finds external transactions matched to selected Clearwater transactions',
      options: {
        scope: ClearwaterTransactionsKey,
      },
    },
  ],
  [
    'find-matched-cw-trans',
    {
      key: 'alt+m',
      name: 'Find matched Clearwater transactions',
      description:
        'Finds clearwater transactions matched to selected external transactions',
      options: {
        scope: ExternalTransactionsKey,
      },
    },
  ],
  [
    'select-all-cw-trans',
    {
      key: 'ctrl+a',
      name: 'Select all Clearwater transactions',
      description: 'Will select all Clearwater transactions.',
      options: {
        scope: ClearwaterTransactionsKey,
      },
    },
  ],
  [
    'select-all-ext-trans',
    {
      key: 'ctrl+a',
      name: 'Select all External transactions',
      description: 'Will select all external transactions.',
      options: {
        scope: ExternalTransactionsKey,
      },
    },
  ],
  ...prepareHotKeyObjectsCw(),
  ...prepareHotKeyObjectsExt(),
  ...prepareHotKeyObjectsAccountList(),
  ...prepareHotKeyObjectsExtFileViewer(),
]

/**
 *To create Hot Keys Object for Cw Transaction panel
 */
export function prepareHotKeyObjectsCw() {
  const cwHotKey: [HeliosHotkeysType, HotkeyMetadata][] = []
  const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
  for (let i = 0; i < ordinals.length; i++) {
    cwHotKey.push([
      `highlight-row-with-${ordinals[i]}-color-in-cw-transactions` as HighlightRowsInCWTransactions,
      {
        key: `alt+${i + 1}`,
        name: `Highlight selected row in CW Transactions with ${ordinals[i]} color`,
        options: { scope: ClearwaterTransactionsKey },
      } as HotkeyMetadata,
    ])
  }
  return cwHotKey
}
/**
 *To create Hot Keys Object for Ext Transaction panel
 */
export function prepareHotKeyObjectsAccountList() {
  const extHotKey: [HeliosHotkeysType, HotkeyMetadata][] = []
  const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
  for (let i = 0; i < ordinals.length; i++) {
    extHotKey.push([
      `highlight-row-with-${ordinals[i]}-color-in-account-list` as HighlightRowsInAccountList,
      {
        key: `alt+${i + 1}`,
        name: `Highlight selected row in Account List with ${ordinals[i]} color`,
        options: { scope: AccountListKey },
      } as HotkeyMetadata,
    ])
  }
  return extHotKey
}
/**
 *To create Hot Keys Object for Ext Transaction panel
 */
export function prepareHotKeyObjectsExt() {
  const extHotKey: [HeliosHotkeysType, HotkeyMetadata][] = []
  const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
  for (let i = 0; i < ordinals.length; i++) {
    extHotKey.push([
      `highlight-row-with-${ordinals[i]}-color-in-ext-transactions` as HighlightRowsInExtTransactions,
      {
        key: `alt+${i + 1}`,
        name: `Highlight selected row in ext Transactions with ${ordinals[i]} color`,
        options: { scope: ExternalTransactionsKey },
      } as HotkeyMetadata,
    ])
  }
  return extHotKey
}
/**
 *To create Hot Keys Object for Ext Transaction panel
 */
export function prepareHotKeyObjectsExtFileViewer() {
  const extFileViewerDetailHotKey: [HeliosHotkeysType, HotkeyMetadata][] = []
  const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
  for (let i = 0; i < ordinals.length; i++) {
    extFileViewerDetailHotKey.push([
      `highlight-row-with-${ordinals[i]}-color-in-external-file-viewer` as HighlightRowsInExternalFileViewer,
      {
        key: `alt+${i + 1}`,
        name: `Highlight selected row in External File viewer with ${ordinals[i]} color`,
        options: { scope: ExternalFileViewerKey },
      } as HotkeyMetadata,
    ])
  }
  return extFileViewerDetailHotKey
}

/**
 *A function to return hotkeys
 */
export function getAvailableHotkeys(): Map<HeliosHotkeysType, HotkeyMetadata> {
  const availableHotKeys = HeliosHotkeys.filter(
    h =>
      !(
        environment.production &&
        HotkeysInDevelopmentAndStaging.includes([...h][0] as HeliosHotkeysType)
      ),
  )
  return new Map<HeliosHotkeysType, HotkeyMetadata>(availableHotKeys)
}
